import React from "react";
import {
  Alert,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Switch,
  Typography
} from "antd";
import { useTranslation } from "react-i18next";
import MoneyInput from "../shared/MoneyInput";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { BUNDESLAENDER, DUNNING_STAGES, formatNumberPreview, isValidIban } from "./constants";

const { Text } = Typography;

const ibanRule = t => ({
  validator: (_, value) =>
    isValidIban(value)
      ? Promise.resolve()
      : Promise.reject(new Error(t("Please enter a valid IBAN")))
});

/** Step 1: Steuernummer, Finanzamt, USt-IdNr, Bundesland, Kleinunternehmer, Bauleistender, cash. */
export const CompanySection = () => {
  const { t } = useTranslation("accounting");
  const kleinunternehmer = Form.useWatch("kleinunternehmer");
  const bauleistender = Form.useWatch("bauleistender");
  return (
    <>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="taxNumber"
            label={t("Tax number (Steuernummer)")}
            rules={[{ required: true, message: t("Please enter the tax number") }]}
          >
            <Input placeholder="12/345/67890" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="finanzamt" label={t("Tax office (Finanzamt)")}>
            <Input placeholder={t("e.g. Finanzamt München")} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="vatId"
            label={t("VAT ID (USt-IdNr.)")}
            rules={[
              { pattern: /^(DE\d{9})?$/, message: t("A German VAT ID is DE followed by 9 digits") }
            ]}
            normalize={v =>
              String(v || "")
                .replace(/\s+/g, "")
                .toUpperCase()
            }
          >
            <Input placeholder="DE123456789" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="bundesland"
            label={t("Federal state (Bundesland)")}
            rules={[{ required: true, message: t("Please choose the federal state") }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={BUNDESLAENDER.map(b => ({ value: b.code, label: `${b.name} (${b.code})` }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="kleinunternehmer"
        label={t("Small business (Kleinunternehmer, § 19 UStG)")}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      <Alert
        type={kleinunternehmer ? "warning" : "info"}
        showIcon
        style={{ marginBottom: 16 }}
        message={t("Kleinunternehmer thresholds")}
        description={t(
          "You may use the small business rule when last year's turnover did not exceed 25,000 EUR and this year's turnover will not exceed 100,000 EUR. Invoices then carry no VAT and no input tax can be deducted. Once the 100,000 EUR limit is crossed, VAT applies from that invoice on."
        )}
      />

      <Form.Item
        name="bauleistender"
        label={t("We perform construction services (Bauleistungen, § 13b UStG / § 48 EStG)")}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      {bauleistender ? (
        <>
          <Text type="secondary">
            {t(
              "Your own exemption certificate (Freistellungsbescheinigung § 48b EStG), so customers do not withhold 15 % construction tax."
            )}
          </Text>
          <Row gutter={16} style={{ marginBlockStart: 8 }}>
            <Col xs={24} md={12}>
              <Form.Item
                name={["freistellungsbescheinigung", "number"]}
                label={t("Certificate number")}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={["freistellungsbescheinigung", "finanzamt"]}
                label={t("Issuing tax office")}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={["freistellungsbescheinigung", "validFrom"]} label={t("Valid from")}>
                <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={["freistellungsbescheinigung", "validUntil"]}
                label={t("Valid until")}
              >
                <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
          </Row>
        </>
      ) : null}

      <Form.Item
        name="handlesCash"
        label={t("We handle cash (Kassenbuch required)")}
        valuePropName="checked"
        extra={t(
          "Cash businesses must keep a daily cash book; the cash book module is switched on for you."
        )}
      >
        <Switch />
      </Form.Item>
    </>
  );
};

/** Step 2: Kontenrahmen, accounting method, fiscal year, UStVA interval, tax office bank details. */
export const BookkeepingSection = ({ chartLocked, charts = ["skr03", "skr04"] }) => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const monthNames = Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(undefined, { month: "long" }).format(new Date(2026, i, 1))
  );
  return (
    <>
      <Form.Item
        name="kontenrahmen"
        label={t("Chart of accounts (Kontenrahmen)")}
        extra={
          chartLocked
            ? t("The chart of accounts cannot change once journal entries exist.")
            : t(
                "SKR03 is ordered by business process, SKR04 by balance sheet structure. Most tax advisors of craft businesses work with SKR03; ask yours before choosing."
              )
        }
      >
        <Radio.Group
          disabled={chartLocked}
          optionType="button"
          buttonStyle="solid"
          options={charts.map(c => ({ value: c, label: c.toUpperCase() }))}
        />
      </Form.Item>

      <Form.Item
        name="accountingMethod"
        label={t("Accounting method")}
        extra={t(
          "EÜR (Einnahmenüberschussrechnung): income and expenses count when paid; allowed up to 800,000 EUR turnover and 80,000 EUR profit. Bilanz: double-entry with balance sheet, required for registered merchants (e.K., GmbH) and above the thresholds. The method can only change at a year change."
        )}
      >
        {/* Long labels: plain radios on phones so they can wrap instead of overflowing. */}
        <Radio.Group
          optionType={isMobile ? "default" : "button"}
          buttonStyle="solid"
          options={[
            { value: "euer", label: t("EÜR (cash basis)") },
            { value: "bilanz", label: t("Bilanz (accrual basis)") }
          ]}
        />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item name="fiscalYearStartMonth" label={t("Fiscal year starts in")}>
            <Select options={monthNames.map((name, i) => ({ value: i + 1, label: name }))} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="ustvaInterval"
            label={t("VAT return interval (Voranmeldungszeitraum)")}
            extra={t(
              "Monthly when last year's VAT exceeded 9,000 EUR, quarterly below, none up to 2,000 EUR or as a Kleinunternehmer."
            )}
          >
            <Select
              options={[
                { value: "monthly", label: t("Monthly") },
                { value: "quarterly", label: t("Quarterly") },
                { value: "yearly", label: t("Yearly (annual return only)") },
                { value: "none", label: t("None") }
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="dauerfristverlaengerung"
            label={t("Permanent extension (Dauerfristverlängerung)")}
            valuePropName="checked"
            extra={t(
              "Filing deadline moves one month later; monthly filers pay a 1/11 special prepayment."
            )}
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item name="taxOfficeName" label={t("Tax office account holder (for VAT payments)")}>
            <Input placeholder={t("e.g. Finanzamt München, Landeshauptkasse")} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="taxOfficeIban"
            label={t("Tax office IBAN")}
            rules={[ibanRule(t)]}
            normalize={v => String(v || "").toUpperCase()}
          >
            <Input placeholder="DE00 0000 0000 0000 0000 00" />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

// optional: an empty format is allowed and means `emptyHint` (e.g. "same range as invoices").
const NumberFormatItem = ({
  name,
  label,
  sampleNumber,
  optional = false,
  emptyHint,
  placeholder
}) => {
  const { t } = useTranslation("accounting");
  const pattern = Form.useWatch(name);
  const empty = !String(pattern || "").trim();
  return (
    <Form.Item
      name={name}
      label={label}
      rules={[
        ...(optional ? [] : [{ required: true, message: t("Please enter a number format") }]),
        { pattern: /\{N(:\d+)?\}/, message: t("The format must contain {N} or {N:5}") }
      ]}
      extra={
        optional && empty ? (
          <span>{emptyHint}</span>
        ) : (
          <span>
            {t("Preview")}: <Text code>{formatNumberPreview(pattern, sampleNumber)}</Text>
          </span>
        )
      }
    >
      <Input placeholder={placeholder || "RE-{YYYY}-{N:5}"} allowClear={optional} />
    </Form.Item>
  );
};

/** Step 3: number formats with live preview, payment terms, Skonto, SEPA creditor id. */
export const InvoicesSection = ({ sequences }) => {
  const { t } = useTranslation("accounting");
  const start = Form.useWatch("invoiceNumberStart");
  const nextInvoice = Math.max(Number(start) || 1, (sequences?.invoice || 0) + 1);
  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t(
          "Placeholders: {YYYY} year, {YY} short year, {MM} month, {N} running number, {N:5} zero-padded to 5 digits. Numbers are gapless per year and assigned when an invoice is sent."
        )}
      />
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <NumberFormatItem
            name="invoiceNumberFormat"
            label={t("Invoice number format")}
            sampleNumber={nextInvoice}
          />
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="invoiceNumberStart"
            label={t("First invoice number this year")}
            extra={
              sequences?.invoice
                ? t(
                    "Numbers up to {{n}} were already assigned this year; the start can only move above that.",
                    { n: sequences.invoice }
                  )
                : null
            }
          >
            <InputNumber min={1} precision={0} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <NumberFormatItem
            name="belegNumberFormat"
            label={t("Receipt number format (incoming documents)")}
            sampleNumber={1}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item name="defaultPaymentTermsDays" label={t("Default payment term (days)")}>
            <InputNumber
              min={0}
              max={120}
              precision={0}
              style={{ width: "100%" }}
              addonAfter={t("days")}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name={["defaultSkonto", "percent"]}
            label={t("Default cash discount (Skonto)")}
          >
            <InputNumber
              min={0}
              max={10}
              step={0.5}
              precision={1}
              style={{ width: "100%" }}
              addonAfter="%"
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name={["defaultSkonto", "days"]} label={t("Skonto within (days)")}>
            <InputNumber
              min={0}
              max={60}
              precision={0}
              style={{ width: "100%" }}
              addonAfter={t("days")}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name="glaeubigerId"
        label={t("SEPA creditor identifier (Gläubiger-Identifikationsnummer)")}
        extra={t("Issued by the Bundesbank; needed only if you collect by SEPA direct debit.")}
        rules={[
          {
            pattern: /^([A-Z]{2}\d{2}[A-Z0-9]{3}\d{11})?$/,
            message: t("Format: DE98ZZZ09999999999")
          }
        ]}
        normalize={v =>
          String(v || "")
            .replace(/\s+/g, "")
            .toUpperCase()
        }
      >
        <Input placeholder="DE98ZZZ09999999999" />
      </Form.Item>
    </>
  );
};

/** Step 4: reminder timing, fees per stage, default interest, automatic sending. */
export const DunningSection = () => {
  const { t } = useTranslation("accounting");
  const autoSend = Form.useWatch(["dunning", "autoSend"]);
  return (
    <>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name={["dunning", "reminderDays"]}
            label={t("Payment reminder after due date (days)")}
            extra={t("Grace period after the due date before the friendly reminder goes out.")}
          >
            <InputNumber
              min={0}
              max={60}
              precision={0}
              style={{ width: "100%" }}
              addonAfter={t("days")}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name={["dunning", "stageDays"]} label={t("Days between dunning stages")}>
            <InputNumber
              min={1}
              max={90}
              precision={0}
              style={{ width: "100%" }}
              addonAfter={t("days")}
            />
          </Form.Item>
        </Col>
      </Row>
      <Text strong>{t("Dunning fee (Mahngebühr) per stage")}</Text>
      <Row gutter={16} style={{ marginBlockStart: 8 }}>
        {DUNNING_STAGES.map((stage, i) => (
          <Col xs={24} sm={12} md={6} key={stage}>
            <Form.Item name={["dunning", "fees", i]} label={t(stage)}>
              <MoneyInput min={0} />
            </Form.Item>
          </Col>
        ))}
      </Row>
      <Text type="secondary">
        {t(
          "Fees must reflect real costs (postage, paper); courts accept roughly 2.50 to 5 EUR. Reminders towards consumers are usually free of charge."
        )}
      </Text>
      <Row gutter={16} style={{ marginBlockStart: 16 }}>
        <Col xs={24} md={8}>
          <Form.Item
            name={["dunning", "interestB2B"]}
            label={t("Default interest B2B (points above base rate)")}
            extra={t("§ 288 (2) BGB: 9 points above the Bundesbank base rate.")}
          >
            <InputNumber min={0} max={20} step={0.5} style={{ width: "100%" }} addonAfter="%" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name={["dunning", "interestB2C"]}
            label={t("Default interest B2C (points above base rate)")}
            extra={t("§ 288 (1) BGB: 5 points above the base rate.")}
          >
            <InputNumber min={0} max={20} step={0.5} style={{ width: "100%" }} addonAfter="%" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name={["dunning", "flatFeeB2B"]}
            label={t("Flat fee B2B (§ 288 (5) BGB)")}
            extra={t("40 EUR lump sum per late business payment.")}
          >
            <MoneyInput min={0} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name={["dunning", "autoSend"]}
        label={t("Send reminders automatically")}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      {autoSend ? (
        <Alert
          type="warning"
          showIcon
          message={t("Reminders will be emailed without review")}
          description={t(
            "Every overdue invoice that is not blocked for dunning gets its reminder or dunning letter sent by the nightly job. Keep this off if you prefer to check each letter first."
          )}
        />
      ) : null}
    </>
  );
};

/** Step 5: tax advisor contact and DATEV numbers for the export. */
export const AdvisorSection = () => {
  const { t } = useTranslation("accounting");
  return (
    <>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item name={["steuerberater", "name"]} label={t("Tax advisor (Steuerberater)")}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name={["steuerberater", "email"]}
            label={t("Email")}
            rules={[{ type: "email", message: t("Please enter a valid email address") }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name={["steuerberater", "datevBeraterNr"]}
            label={t("DATEV advisor number (Berater-Nr.)")}
            rules={[{ pattern: /^\d{0,7}$/, message: t("Up to 7 digits") }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name={["steuerberater", "datevMandantenNr"]}
            label={t("DATEV client number (Mandanten-Nr.)")}
            rules={[{ pattern: /^\d{0,5}$/, message: t("Up to 5 digits") }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Text type="secondary">
        {t(
          "Both numbers appear in the header of DATEV exports so your advisor can import them directly."
        )}
      </Text>
    </>
  );
};

/** Tab: number ranges of the other documents, invoice texts, payment differences, notices. */
export const DocumentsSection = () => {
  const { t } = useTranslation("accounting");
  const ranges = [
    {
      name: "creditNoteNumberFormat",
      label: t("Correction invoice (Rechnungskorrektur)"),
      placeholder: "RK-{YYYY}-{N:5}"
    },
    {
      name: "cancellationNumberFormat",
      label: t("Cancellation invoice (Stornorechnung)"),
      placeholder: "ST-{YYYY}-{N:5}",
      optional: true,
      emptyHint: t("Empty: cancellations use the invoice number range.")
    },
    {
      name: "correctionNoticeNumberFormat",
      label: t("Correction notice (Rechnungsberichtigung)"),
      placeholder: "RB-{YYYY}-{N:5}"
    },
    {
      name: "selfBillingNumberFormat",
      label: t("Self-billing credit note (Gutschrift § 14 (2) UStG)"),
      placeholder: "GSV-{YYYY}-{N:5}"
    },
    { name: "offerNumberFormat", label: t("Quote (Angebot)"), placeholder: "AN-{YYYY}-{N:5}" },
    {
      name: "orderConfirmationNumberFormat",
      label: t("Order confirmation (Auftragsbestätigung)"),
      placeholder: "AB-{YYYY}-{N:5}"
    }
  ];
  return (
    <>
      <Typography.Title level={5}>{t("Number ranges")}</Typography.Title>
      <Text type="secondary">
        {t(
          "Each document type has its own gapless range per year. Placeholders: {YYYY}, {YY}, {MM}, {N}, {N:5}."
        )}
      </Text>
      <Row gutter={16} style={{ marginBlockStart: 8 }}>
        {ranges.map(r => (
          <Col xs={24} md={12} key={r.name}>
            <NumberFormatItem sampleNumber={1} {...r} />
          </Col>
        ))}
      </Row>

      <Typography.Title level={5}>{t("Invoice texts")}</Typography.Title>
      <Form.Item
        name="invoiceHeaderText"
        label={t("Text above the items")}
        extra={t("Printed on every invoice before the line items, e.g. a greeting.")}
      >
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={2000} showCount />
      </Form.Item>
      <Form.Item
        name="invoiceFooterText"
        label={t("Text below the totals")}
        extra={t("Printed after the totals, e.g. thanks or warranty notes.")}
      >
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={2000} showCount />
      </Form.Item>

      <Typography.Title level={5}>{t("Payment differences")}</Typography.Title>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="smallDifferenceLimit"
            label={t("Write off small differences up to")}
            extra={t(
              "A shortfall up to this amount can be closed as a payment difference without a correction invoice; the VAT is corrected proportionally."
            )}
          >
            <MoneyInput min={0} max={100} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="directDebitPrenotificationDays"
            label={t("Direct debit pre-notification (days)")}
            extra={t(
              "Days between the pre-notification on the invoice and the collection; 14 by SEPA default, shorter only if agreed with the customer."
            )}
          >
            <InputNumber
              min={1}
              max={30}
              precision={0}
              style={{ width: "100%" }}
              addonAfter={t("days")}
            />
          </Form.Item>
        </Col>
      </Row>

      <Typography.Title level={5}>{t("Craft business notices")}</Typography.Title>
      <Form.Item
        name="labourCostStatement"
        label={t("Show labour costs separately for private customers (§ 35a EStG)")}
        valuePropName="checked"
        extra={t(
          "Private customers can deduct 20 % of the labour, machine and travel costs from their income tax; the invoice states that share."
        )}
      >
        <Switch />
      </Form.Item>
      <Form.Item
        name="propertyRelatedWork"
        label={t("We work on buildings and land (§ 14b UStG retention notice)")}
        valuePropName="checked"
        extra={t(
          "Invoices to private customers then state that they must keep the invoice for two years."
        )}
      >
        <Switch />
      </Form.Item>
      <Form.Item
        name="girocode"
        label={t("Print a GiroCode on invoices and payment reminders")}
        valuePropName="checked"
        extra={t(
          "A QR code that banking apps scan to fill in the transfer (recipient, IBAN, open amount, invoice number). Not printed on credit notes, cancellations or invoices paid by direct debit."
        )}
      >
        <Switch />
      </Form.Item>
    </>
  );
};
